import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import CommonDateInput from '../../CommonComponents/dateInput/dateInputView';
import { isEmpty, isNumeric } from '../../../js/app/react/validators';
import { determineInputHTMLClass } from '../SimpleSelect/SimpleSelectView';
import withResponsiveness from '../../CommonComponents/withResponsiveness';

export const LEFT = 1;
export const CENTER = 2;
export const RIGHT = 3;
const EMPTY_HANDLER = () => {};

const formatDate = date => moment.utc(new Date(date)).format('MM/DD/YYYY');

function determineInputHTMLStyle(alignment, paddingLeft, paddingRight) {
  switch (alignment) {
    case LEFT:
      if (paddingLeft === null) {
        return {};
      }
      return {
        left: '0px',
      };
    case CENTER:
      return {
        left: '50%',
      };
    case RIGHT:
      if (paddingRight === null) {
        return {};
      }
      return {
        right: '0px',
      };
    default:
      throw Error('Unexpected alignment');
  }
}

class DateInput extends Component {
  constructor() {
    super();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onInputRef = this.onInputRef.bind(this);
    this.onPostRender = this.onPostRender.bind(this);
    this.onChangeDate = this.onChangeDate.bind(this);
    this.onNativeDateChanged = this.onNativeDateChanged.bind(this);
    this.state = {
      paddingRight: null,
      paddingLeft: null,
      focused: false,
    };
  }

  componentDidMount() {
    this.onPostRender();
  }

  componentDidUpdate() {
    this.onPostRender();
  }

  onFocus(event) {
    this.setState({
      focused: true,
    });
    const { onFocus } = this.props;
    onFocus(event);
  }

  onBlur(event) {
    this.setState({
      focused: false,
    });
    const { onBlur } = this.props;
    onBlur(event);
  }

  onInputRef(inputElement) {
    this.inputElement = inputElement;
    const { innerRef } = this.props;
    innerRef(inputElement);
  }

  onPostRender() {
    const { ADV } = window;
    const { paddingRight, paddingLeft } = this.state;
    if (this.inputElement) {
      const newPaddingRight = parseFloat(ADV.util.getCss(this.inputElement, 'paddingRight'));
      if (isNumeric(newPaddingRight)) {
        if (paddingRight !== newPaddingRight) {
          this.setState({
            paddingRight: newPaddingRight,
          });
        }
      }
      const newPaddingLeft = parseFloat(ADV.util.getCss(this.inputElement, 'paddingLeft'));
      if (isNumeric(newPaddingLeft)) {
        if (paddingLeft !== newPaddingLeft) {
          this.setState({
            paddingLeft: newPaddingLeft,
          });
        }
      }
    }
  }

  onChangeDate(date) {
    const dateFormatted = unformattedDate => {
      if (unformattedDate instanceof Date) {
        return formatDate(unformattedDate);
      }
      return unformattedDate;
    };
    const { onChange } = this.props;
    onChange(dateFormatted(date));
  }

  onNativeDateChanged({ target: { value } }) {
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
    const parts = value.split('-');
    const { onChange } = this.props;
    onChange(`${parts[1]}/${parts[2]}/${parts[0]}`);
  }

  render() {
    const {
      value,
      placeholder,
      placeholderClassName,
      alignment,
      focusedClassName,
      isUnder,
      htmlPlaceholder,
      id,
      viewport: { isMobile },
    } = this.props;
    const { focused, paddingRight, paddingLeft } = this.state;

    const parsedValue = formatDate(value);

    return (
      <div className="adv-workflow-input">
        {!htmlPlaceholder && (
          <div
            className={determineInputHTMLClass(
              focused || !isEmpty(parsedValue),
              isUnder,
              focusedClassName,
              alignment,
              placeholderClassName,
            )}
            style={determineInputHTMLStyle(alignment, paddingLeft, paddingRight)}
            id={id}
          >
            {placeholder}
          </div>
        )}
        <CommonDateInput
          {...this.props}
          key={parsedValue}
          onFocus={isMobile ? EMPTY_HANDLER : this.onFocus}
          onBlur={isMobile ? EMPTY_HANDLER : this.onBlur}
          onChange={isMobile ? EMPTY_HANDLER : this.onChangeDate}
          classNames={{
            container: 'DayPickerInput adv-input-date',
            overlayWrapper: 'DayPickerInput-OverlayWrapper',
            overlay: 'DayPickerInput-Overlay',
          }}
          placeholder={htmlPlaceholder === '' ? ' ' : htmlPlaceholder}
        />
        {isMobile && (
          <input
            type="date"
            onChange={this.onNativeDateChanged}
            className="native-input"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          />
        )}
      </div>
    );
  }
}
DateInput.defaultProps = {
  isUnder: false,
  onFocus: EMPTY_HANDLER,
  onBlur: EMPTY_HANDLER,
  innerRef: EMPTY_HANDLER,
  placeholderClassName: '',
  focusedClassName: 'adv-workflow-input-placeholder-focused',
  alignment: LEFT,
  htmlPlaceholder: '',
  value: '',
};

DateInput.propTypes = {
  value: PropTypes.string,
  isUnder: PropTypes.bool,
  viewport: PropTypes.shape({ isMobile: PropTypes.bool.isRequired }).isRequired,
  placeholderClassName: PropTypes.string,
  focusedClassName: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  innerRef: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  alignment: PropTypes.oneOf([LEFT, CENTER, RIGHT]),
  htmlPlaceholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default withResponsiveness(DateInput);
